import React, { useState, useRef, useEffect } from "react";
import { Search, Bell, Clock, Menu, ChevronDown } from "lucide-react";
import { searchStockName } from "../../api/api";
import ProfileMenu from "./profileMenu";
import { useNavigate } from "react-router-dom";
import TimeoutSnackbar from "../common/TimeoutSnackbar";
import { refreshToken } from "../../api/auth_api";
import ThemeToggle from "../common/ThemeToggle";
import { useMarket } from "../../context/MarketContext";
import { getMarkets } from "../../api/stocks_api";
import { CURRENCY_SYMBOLS } from "../../utils/currency";

// ── Outside Header ────────────────────────────────────────────────────────────

const ENABLED_MARKETS = new Set(["US", "TR", "IN", "ALL"]);

const COUNTRY_SHORT = {
  "United States": "US",
  "United Kingdom": "UK",
};

const getOptionLabel = (m) => {
  const symbol = m.currency ? (CURRENCY_SYMBOLS[m.currency] ?? m.currency) : null;

  const countryName = COUNTRY_SHORT[m.name] || m.name;

  return symbol
    ? `${countryName}: ${symbol}`
    : `${m.flag_emoji} ${countryName}`;
};

const MarketDropdown = ({ markets, market, setMarket }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = markets.find((m) => m.code === market);

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          backgroundColor: "var(--color-surface)",
          border: `1px solid ${open ? "var(--color-primary)" : "var(--color-border-primary)"}`,
          color: "var(--color-text-primary)",
          borderRadius: "var(--radius-lg)",
          fontSize: "var(--font-size-sm)",
          fontWeight: "var(--font-weight-semibold)",
          padding: "8px 10px",
          cursor: "pointer",
          minWidth: "auto",
          justifyContent: "space-between",
          transition: "var(--transition-fast)",
          boxShadow: open ? "0 0 0 2px rgba(0,150,255,0.2)" : "none",
        }}
      >
        <span>{selected ? getOptionLabel(selected) : "Select"}</span>
        <ChevronDown
          size={14}
          style={{
            color: "var(--color-text-muted)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "var(--transition-fast)",
            flexShrink: 0,
          }}
        />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            minWidth: "100%",
            backgroundColor: "var(--color-surface)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 99999,
            overflow: "hidden",
          }}
        >
          {markets.map((m) => {
            const enabled = ENABLED_MARKETS.has(m.code);
            const isSelected = m.code === market;
            return (
              <div
                key={m.code}
                onClick={() => {
                  if (!enabled) return;
                  setMarket(m.code);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 14px",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  cursor: enabled ? "pointer" : "not-allowed",
                  color: isSelected
                    ? "var(--color-text-inverse)"
                    : enabled
                      ? "var(--color-text-primary)"
                      : "var(--color-text-muted)",
                  backgroundColor: isSelected ? "var(--color-primary)" : "transparent",
                  // opacity: enabled ? 1 : 0.4,
                  transition: "var(--transition-fast)",
                  userSelect: "none",
                }}
                onMouseEnter={(e) => {
                  if (!enabled || isSelected) return;
                  e.currentTarget.style.backgroundColor = "var(--color-surface-hover)";
                }}
                onMouseLeave={(e) => {
                  if (!enabled || isSelected) return;
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                {getOptionLabel(m)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Header component ──────────────────────────────────────────────────────────

const Header = ({ isMobileOpen, setIsMobileOpen }) => {
  const [searchValue, setSearchValue] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const { market, setMarket } = useMarket();
  const [currentTime, setCurrentTime] = useState("");
  const [showClockTooltip, setShowClockTooltip] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const res = await getMarkets();
        if (res.authExpired) { setRedirecting(true); return; }
        if (res.success && res.data?.markets?.length) {
          setMarkets(res.data.markets);
          if (!market) setMarket(res.data.default ?? "US");
        }
      } catch (err) {
        console.error("Failed to fetch markets:", err);
      } finally {
        setMarketsLoading(false);
      }
    };
    fetchMarkets();
  }, []);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString(undefined, {
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setCurrentTime(`${timeString} (${timeZone})`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      setResults([]); setErrorMsg(""); setNoResults(false);
      return;
    }
    const delay = setTimeout(async () => {
      setLoading(true); setErrorMsg(""); setNoResults(false);
      try {
        const res = await searchStockName(searchValue.trim());
        if (res.authExpired) {
          const refreshed = await refreshToken();
          if (refreshed.success) {
            const retryRes = await searchStockName(searchValue.trim());
            if (retryRes.success && Array.isArray(retryRes.data)) {
              setResults(retryRes.data); return;
            }
          }
          setRedirecting(true); return;
        }
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          setResults(res.data);
        } else {
          setResults([]); setNoResults(true);
        }
      } catch { setErrorMsg("Something went wrong while searching."); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(delay);
  }, [searchValue]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setResults([]); setNoResults(false); setErrorMsg("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (!token) setRedirecting(true);
  }, []);

  useEffect(() => {
    if (!redirecting) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          sessionStorage.clear();
          navigate("/login", { replace: true });
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [redirecting]);

  return (
    <header
      className="border-b px-3 sm:px-6 py-3 sm:py-4 sticky top-0 z-30 w-full"
      style={{
        backgroundColor: "var(--color-bg-primarySideBarHeader)",
        borderBottomColor: "var(--color-border-primary)",
      }}
    >
      <div className="flex items-center justify-between">
        {/* ── Left Side ── */}
        <div className="flex items-center flex-1 max-w-3xl gap-2 sm:gap-3">
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden p-2.5 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
            style={{ transition: "var(--transition-fast)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <Menu size={24} style={{ color: "var(--color-text-secondary)" }} />
          </button>

          {/* Search */}
          <div ref={searchRef} className="relative flex-1 min-w-0">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2"
              size={20}
              style={{ color: "var(--color-text-muted)" }}
            />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => { setSearchValue(e.target.value); setHighlightedIndex(-1); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" && results.length > 0) {
                  e.preventDefault();
                  setHighlightedIndex((prev) => Math.min(prev + 1, results.length - 1));
                } else if (e.key === "ArrowUp" && results.length > 0) {
                  e.preventDefault();
                  setHighlightedIndex((prev) => Math.max(prev - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  if (highlightedIndex >= 0 && results[highlightedIndex]) {
                    const item = results[highlightedIndex];
                    setSearchValue(""); setResults([]); setHighlightedIndex(-1);
                    navigate(`/stocks/${item.ticker}/details`);
                  } else if (results.length === 1) {
                    setSearchValue(""); setResults([]); setHighlightedIndex(-1);
                    navigate(`/stocks/${results[0].ticker}/details`);
                  }
                } else if (e.key === "Escape") {
                  setResults([]); setHighlightedIndex(-1);
                }
              }}
              placeholder="Search ticker or company..."
              className="w-full pl-10 pr-4 py-2.5 min-h-[44px]"
              role="combobox"
              aria-expanded={results.length > 0}
              aria-autocomplete="list"
              style={{
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                borderRadius: "var(--radius-lg)",
                fontSize: "var(--font-size-sm)",
                transition: "var(--transition-fast)",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "var(--color-primary)";
                e.currentTarget.style.boxShadow = "0 0 0 2px rgba(0, 150, 255, 0.2)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "var(--color-border-primary)";
                e.currentTarget.style.boxShadow = "none";
              }}
            />

            {loading && (
              <div className="absolute top-full mt-2 w-full z-[99999]"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-lg)",
                  padding: "var(--spacing-md)",
                }}>
                <div style={{
                  fontSize: "var(--font-size-sm)", color: "var(--color-primary)",
                  borderBottom: "1px solid var(--color-border-secondary)",
                  padding: "var(--spacing-sm) var(--spacing-md)",
                }}>
                  Searching all results for "{searchValue}" »
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-4 rounded animate-pulse mt-2"
                    style={{ backgroundColor: "var(--color-surface-hover)", borderRadius: "var(--radius-sm)" }} />
                ))}
              </div>
            )}

            {!loading && errorMsg && (
              <div className="absolute top-full mt-2 w-full z-[99999]"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-error)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-md)",
                  padding: "var(--spacing-md)",
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-error)",
                }}>
                {errorMsg}
              </div>
            )}

            {!loading && noResults && !errorMsg && (
              <div className="absolute top-full mt-2 w-full z-[99999]"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-md)",
                  padding: "var(--spacing-md)",
                  fontSize: "var(--font-size-sm)",
                  color: "var(--color-text-secondary)",
                }}>
                No results found
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className="absolute top-full mt-2 w-full z-[99999] overflow-hidden"
                style={{
                  backgroundColor: "var(--color-surface)",
                  border: "1px solid var(--color-border-primary)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-lg)",
                }}>
                <div className="flex justify-between items-center"
                  style={{
                    padding: "var(--spacing-sm) var(--spacing-md)",
                    backgroundColor: "var(--color-bg-secondary)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--color-text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  <span>Symbols</span>
                </div>
                <div className="max-h-64 overflow-auto">
                  {results.map((item, index) => (
                    <div
                      key={item.ticker}
                      className="cursor-pointer"
                      style={{
                        padding: "var(--spacing-md)",
                        borderBottom: index !== results.length - 1 ? "1px solid var(--color-border-secondary)" : "none",
                        transition: "var(--transition-fast)",
                        backgroundColor: index === highlightedIndex ? "var(--color-surface-hover)" : "transparent",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--color-surface-hover)"; setHighlightedIndex(index); }}
                      onMouseLeave={(e) => { if (index !== highlightedIndex) e.currentTarget.style.backgroundColor = "transparent"; }}
                      onClick={() => { setSearchValue(""); setResults([]); setHighlightedIndex(-1); navigate(`/stocks/${item.ticker}/details`); }}
                    >
                      <div className="flex items-center gap-3">

                        <div
                          style={{
                            minWidth: "110px",
                            maxWidth: "110px",
                            fontWeight: "var(--font-weight-semibold)",
                            fontSize: "var(--font-size-sm)",
                            color: "var(--color-text-primary)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {item.ticker}
                        </div>

                        <div
                          style={{
                            flex: 1,
                            fontSize: "var(--font-size-sm)",
                            color: "var(--color-text-secondary)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {item.company_name}
                        </div>

                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Market Dropdown ── */}
          {marketsLoading ? (
            <div className="animate-pulse" style={{
              width: "130px", height: "38px",
              borderRadius: "var(--radius-lg)",
              backgroundColor: "var(--color-surface-hover)",
            }} />
          ) : (
            <MarketDropdown markets={markets} market={market} setMarket={setMarket} />
          )}
        </div>

        {/* ── Right Side ── */}
        <div className="flex items-center gap-1 sm:gap-2 md:gap-4 ml-2 sm:ml-3 md:ml-6">
          <ThemeToggle />

          <div className="relative hidden sm:block"
            onMouseEnter={() => setShowClockTooltip(true)}
            onMouseLeave={() => setShowClockTooltip(false)}>
            <button className="p-2 rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center" style={{ transition: "var(--transition-fast)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
              <Clock size={20} style={{ color: "var(--color-text-primary)" }} />
            </button>
            {showClockTooltip && (
              <div style={{
                position: "absolute", top: "120%", right: 0,
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: "var(--radius-lg)",
                padding: "6px 10px",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-primary)",
                boxShadow: "var(--shadow-md)",
                whiteSpace: "nowrap",
                zIndex: 9999,
              }}>
                {currentTime}
              </div>
            )}
          </div>

          <button className="hidden sm:flex p-2 rounded-lg relative min-w-[44px] min-h-[44px] items-center justify-center" style={{ transition: "var(--transition-fast)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
            <Bell size={20} style={{ color: "var(--color-text-primary)" }} />
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full"
              style={{ backgroundColor: "var(--color-error)" }} />
          </button>

          <ProfileMenu />
        </div>
      </div>

      {redirecting && <TimeoutSnackbar open={redirecting} countdown={countdown} />}
    </header>
  );
};

export default Header;